import { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { toast } from "react-toastify"
import { Modal, ModalBody, ModalFooter, Button, Spinner } from "reactstrap"
import { useAuth } from "hooks/authHook"
import {
  useUpdateProfileImage,
  useDeleteProfileImage,
} from "hooks/userHook"
import { compressImage, formatBytes } from "utils/imageCompression"

const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"]
const MAX_SIZE_BYTES = 5 * 1024 * 1024 // 5 MB

/**
 * Resolves a user record to a usable profile-image URL string,
 * or null when no image is set. Supports several backend shapes:
 *   - { profile_image: [{ id, url }] }
 *   - { profile_image: { id, url } }
 *   - { profile_image: "<url string>" }
 *   - { avatar: "<url>" | { url } }
 */
export const getProfileImageUrl = user => {
  if (!user) return null

  const candidates = [user.profile_image, user.profileImage, user.avatar]
  for (const raw of candidates) {
    if (!raw) continue
    if (typeof raw === "string") return raw
    if (Array.isArray(raw) && raw.length) {
      const first = raw[0]
      if (typeof first === "string") return first
      if (first?.url) return first.url
    }
    if (typeof raw === "object") {
      if (raw.url) return raw.url
      if (raw.path) return raw.path
    }
  }
  return null
}

const ProfileImageUploader = ({
  user,
  size = 104,
  className = "",
  borderWidth = 3,
}) => {
  const { user: currentUser } = useAuth()
  const fileInputRef = useRef(null)

  const [previewUrl, setPreviewUrl] = useState(null)
  const [pendingFile, setPendingFile] = useState(null)
  const [pendingMeta, setPendingMeta] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [showActions, setShowActions] = useState(false)
  const [isCompressing, setIsCompressing] = useState(false)

  const updateMutation = useUpdateProfileImage()
  const deleteMutation = useDeleteProfileImage()

  const isOwnProfile =
    !!currentUser && !!user && currentUser.id === user.id

  const currentImageUrl = getProfileImageUrl(user)

  // Clean up object URLs when preview changes/unmounts
  useEffect(() => {
    if (!pendingFile) {
      setPreviewUrl(null)
      return undefined
    }
    const objectUrl = URL.createObjectURL(pendingFile)
    setPreviewUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [pendingFile])

  const resetPending = () => {
    setPendingFile(null)
    setPendingMeta(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  if (!isOwnProfile) {
    return null
  }

  const initial = (
    user?.name ||
    user?.username ||
    currentUser?.username ||
    "U"
  )
    .charAt(0)
    .toUpperCase()

  const displayUrl = previewUrl || currentImageUrl

  const validateFile = file => {
    if (!file) return "No file selected"
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return "Only JPG, PNG, or WEBP images are allowed."
    }
    if (file.size > MAX_SIZE_BYTES) {
      return "Image must be 5 MB or smaller."
    }
    return null
  }

  const handleFileChange = async event => {
    const file = event.target.files?.[0]
    if (!file) return
    const error = validateFile(file)
    if (error) {
      toast.error(error)
      event.target.value = ""
      return
    }

    setIsCompressing(true)
    try {
      const result = await compressImage(file, {
        maxWidthOrHeight: 800,
      })
      setPendingFile(result.file)
      setPendingMeta({
        originalSize: result.originalSize,
        compressedSize: result.compressedSize,
        skipped: result.skipped,
      })
      if (!result.skipped && result.compressedSize < result.originalSize) {
        const savedPct = Math.max(
          0,
          Math.round((1 - result.ratio) * 100)
        )
        toast.success(
          `Image optimized · ${formatBytes(result.originalSize)} → ${formatBytes(
            result.compressedSize
          )} (${savedPct}% smaller)`
        )
      }
    } catch (err) {
      const message = err?.message || "Failed to process image"
      toast.error(message)
      event.target.value = ""
    } finally {
      setIsCompressing(false)
    }
  }

  const handleUpload = async () => {
    if (!pendingFile) return
    try {
      await updateMutation.mutateAsync(pendingFile)
      toast.success("Profile picture updated.")
      resetPending()
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update profile picture."
      toast.error(message)
    }
  }

  const handleCancel = () => {
    resetPending()
  }

  const handleDelete = async () => {
    setConfirmDelete(false)
    try {
      await deleteMutation.mutateAsync()
      toast.success("Profile picture removed.")
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to remove profile picture."
      toast.error(message)
    }
  }

  const isBusy =
    isCompressing || updateMutation.isPending || deleteMutation.isPending
  const showChange = !!currentImageUrl && !pendingFile

  return (
    <>
      <div
        className={`profile-image-uploader position-relative ${className}`}
        style={{ width: size, height: size }}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        <div
          className="rounded-4 overflow-hidden border border-white bg-white shadow-sm w-100 h-100"
          style={{ borderWidth }}
        >
          {displayUrl ? (
            <img
              src={displayUrl}
              alt="Profile"
              className="w-100 h-100"
              style={{ objectFit: "cover" }}
            />
          ) : (
            <div
              className="w-100 h-100 d-flex align-items-center justify-content-center text-primary fw-bold"
              style={{ fontSize: Math.max(20, size * 0.35), background: "#eef2ff" }}
            >
              {initial}
            </div>
          )}

          {isBusy && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                background: "rgba(255,255,255,0.65)",
                backdropFilter: "blur(2px)",
              }}
            >
              <Spinner color="primary" />
            </div>
          )}
        </div>

        {(showActions || isBusy) && !isBusy && (
          <div
            className="profile-image-uploader__overlay position-absolute top-0 start-0 w-100 h-100 rounded-4 d-flex align-items-end justify-content-center"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)",
              padding: 6,
            }}
          >
            <div className="d-flex gap-1">
              <button
                type="button"
                className="btn btn-sm btn-light"
                title="Change photo"
                onClick={() => fileInputRef.current?.click()}
                disabled={isCompressing}
              >
                <i className="bx bx-camera" />
              </button>
              {showChange && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  title="Remove photo"
                  onClick={() => setConfirmDelete(true)}
                  disabled={isCompressing}
                >
                  <i className="bx bx-trash" />
                </button>
              )}
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES.join(",")}
          className="d-none"
          onChange={handleFileChange}
        />
      </div>

      {pendingFile && (
        <Modal isOpen toggle={handleCancel} centered size="md">
          <ModalBody>
            <div className="d-flex align-items-center gap-3">
              <img
                src={previewUrl}
                alt="Preview"
                style={{
                  width: 80,
                  height: 80,
                  objectFit: "cover",
                  borderRadius: 12,
                }}
              />
              <div className="min-width-0 flex-grow-1">
                <h6 className="mb-1">Update profile picture?</h6>
                <p
                  className="text-muted mb-0 text-truncate"
                  style={{ fontSize: 13 }}
                >
                  {pendingFile.name} · {formatBytes(pendingFile.size)}
                </p>
                {pendingMeta && !pendingMeta.skipped && (
                  <p
                    className="text-success mb-0"
                    style={{ fontSize: 12 }}
                  >
                    <i className="bx bx-check-circle me-1" />
                    Compressed from {formatBytes(pendingMeta.originalSize)} (
                    {Math.max(
                      0,
                      Math.round(
                        (1 -
                          pendingMeta.compressedSize /
                            Math.max(1, pendingMeta.originalSize)) *
                          100
                      )
                    )}
                    % smaller)
                  </p>
                )}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              onClick={handleCancel}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleUpload}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Uploading...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      <Modal
        isOpen={confirmDelete}
        toggle={() => setConfirmDelete(false)}
        centered
        size="sm"
      >
        <ModalBody>
          <div className="d-flex align-items-start gap-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center bg-danger bg-opacity-10 text-danger flex-shrink-0"
              style={{ width: 44, height: 44 }}
            >
              <i className="bx bx-trash font-size-20" />
            </div>
            <div>
              <h6 className="mb-1">Remove profile picture?</h6>
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                This will reset your avatar to the default initial.
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={() => setConfirmDelete(false)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <>
                <Spinner size="sm" className="me-2" />
                Removing...
              </>
            ) : (
              "Remove"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

ProfileImageUploader.propTypes = {
  user: PropTypes.object,
  size: PropTypes.number,
  className: PropTypes.string,
  borderWidth: PropTypes.number,
}

export default ProfileImageUploader
